import { exportFlow, getScreensInUse, useImportFlow } from '@api/flows';
import ExportImportButtons from '@components/ExportImportButtons/ExportImportButtons';
import FlowView from '@components/Flows/FlowView';
import TaskConditionsDialog from '@components/Flows/Overlays/TaskConditionsDialog';
import NavigationApprovalDialog from '@components/Forms/NavigationApprovalDialog/NavigationApprovalDialog';
import Loader from '@components/Loader/Loader';
import { Box } from '@mui/system';
import { downloadAsFile } from '@utils/api';
import {
	extractFlowDSL,
	extractGraph,
	extractInteractionId,
	getAllTasks,
	getTaskInteractions,
	getErrorMessage
} from '@utils/flows';
import { stringRequiredValidationScheme } from '@utils/validation';
import { useFormik } from 'formik';
import { useEditorKeyboard, useToast, useRandom } from 'hooks';
import { isEqual, noop } from 'lodash';
import PageEditor from 'pages/PageEditor';
import ActionsBar from 'pages/PageEditor/Editor/ActionsBar';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { ReactFlowInstance, useUpdateNodeInternals } from 'reactflow';
import {
	END_TASK_ID,
	IEdgeData,
	IExportedFlow,
	IFlow,
	IFlowScreens,
	IFlowsMetadata,
	IFlowUpdateRequest,
	IScreenMetadata,
	ITaskNodeData,
	Task,
	TaskType
} from 'types/flows';
import { ToastSeverity } from 'types/toast';
import { object } from 'yup';
import FlowBuilderHeader from './Header';
import FlowTaskDialog from './TaskDialog/FlowTaskDialog';

const FlowBuilder: FC<{
	isLoading: boolean;
	flow?: IFlow;
	screens?: IFlowScreens;
	metadata?: IFlowsMetadata;
	onUpdate: (res: IFlowUpdateRequest) => Promise<IFlow>;
}> = ({ isLoading, flow, screens, metadata, onUpdate }) => {
	const updateNodeInternals = useUpdateNodeInternals();

	const { addToast } = useToast();
	const importFlowMutation = useImportFlow();
	const [key, resetKey] = useRandom();
	const [updateScreenCallback, setUpdateScreenCallback] = useState<
		(() => IScreenMetadata) | null
	>(null);
	const [editingTask, setEditingTask] = useState<Task | null>(null);
	const [isCanvasDirty, setIsCanvasDirty] = useState(false);
	const [isScreenDirty, setIsScreenDirty] = useState(false);
	const [postSaveRefetch, setPostSaveRefetch] = useState(false);
	const [flowInstance, setFlowInstance] = useState<ReactFlowInstance<
		ITaskNodeData,
		IEdgeData
	> | null>(null);

	const { initialNodes, initialEdges } = useMemo(() => {
		if (flow) {
			return extractGraph(
				flow?.dsl,
				{
					onEditTask: setEditingTask
				},
				metadata,
				screens
			);
		}
		return { initialEdges: [], initialNodes: [] };
	}, [flow, metadata, screens, setEditingTask]);

	const readyFlow: IFlow = useMemo(
		() => ({
			id: '',
			version: 0,
			name: 'My New Flow',
			dsl: {
				startTask: '',
				tasks: {}
			},
			disabled: false,
			...flow
		}),
		[flow]
	);

	const formik = useFormik<{
		flow: IFlow;
		screens?: IFlowScreens;
		name: string;
	}>({
		initialValues: { flow: readyFlow, screens, name: readyFlow.name },
		enableReinitialize: true,
		validationSchema: object({
			name: stringRequiredValidationScheme()
		}),
		onSubmit: async (values) => {
			try {
				if (flowInstance) {
					const newTasks = extractFlowDSL(flowInstance, metadata?.actions);
					if (!newTasks) throw Error();
					values.flow.dsl = newTasks;
					values.flow.name = values.name;
					const readyScreens = getScreensInUse(
						values.flow.dsl.tasks!,
						values.screens!
					);
					await onUpdate({
						flow: values.flow,
						screens: readyScreens
					});
					formik.resetForm({ values });
					setIsCanvasDirty(false);
					addToast({
						severity: ToastSeverity.Success,
						content: intl.tr('flows.builder.save.approved')
					});
					setPostSaveRefetch(true);
				}
			} catch (error) {
				addToast({
					severity: ToastSeverity.Error,
					content: getErrorMessage(error, intl.tr('flows.builder.save.failed')),
					error
				});
			}
		}
	});

	const screenFormik = useFormik<{ name: string }>({
		initialValues: { name: editingTask?.name || '' },
		enableReinitialize: true,
		validationSchema: object({
			name: stringRequiredValidationScheme()
		}),
		onSubmit: async (values) => {
			if (updateScreenCallback && editingTask && flowInstance) {
				const screenMetadata = updateScreenCallback?.();
				const screenId = editingTask?.screenId!;
				const currentScreen = formik.values.screens?.[screenId];
				if (!currentScreen) {
					formik.values.screens![screenId] = {
						id: screenId,
						flowId: flow?.id || '',
						version: 0
					};
					// ignore update if no change has happened
				} else if (
					isEqual(currentScreen.htmlTemplate, screenMetadata.htmlTemplate) &&
					values.name === editingTask.name
				) {
					setEditingTask(null);
					return;
				}
				const newScreenState = {
					...formik.values.screens,
					[screenId]: {
						...formik.values.screens![screenId],
						...screenMetadata
					}
				};
				formik.setFieldValue('screens', newScreenState);

				const allTasks = getAllTasks(flowInstance);
				const newInteractions = getTaskInteractions(
					editingTask,
					allTasks,
					metadata,
					newScreenState
				);
				flowInstance.setNodes((allNodes) =>
					allNodes.map((node) => ({
						...node,
						data: {
							...node.data,
							...(node.id === editingTask.id
								? {
										task: { ...editingTask, name: values.name },
										interactions: newInteractions
								  }
								: {})
						}
					}))
				);
				// clean edges of removed interactions
				const interactionIds = newInteractions.map(
					(interaction) => interaction.id
				);
				flowInstance.setEdges((allEdges) =>
					allEdges.filter((edge) => {
						const interactionId = extractInteractionId(edge);
						return (
							edge.source !== editingTask.id ||
							!interactionId ||
							interactionIds.includes(interactionId)
						);
					})
				);
				updateNodeInternals(editingTask.id!);
				setEditingTask(null);
				setIsCanvasDirty(true);
				setIsScreenDirty(false);
			}
		}
	});

	const updateTask = useCallback(
		(task: Task) => {
			if (flowInstance) {
				const allTasks = getAllTasks(flowInstance);
				flowInstance.setNodes((allNodes) => {
					const nodeIndex = allNodes.findIndex((node) => task.id === node.id);
					if (nodeIndex > -1) {
						allNodes[nodeIndex] = {
							...allNodes[nodeIndex],
							data: {
								...allNodes[nodeIndex].data,
								task,
								label: task.name,
								interactions:
									task.type === TaskType.condition
										? getTaskInteractions(
												task,
												allTasks,
												metadata,
												formik.values.screens
										  )
										: allNodes[nodeIndex].data?.interactions
							}
						};
					}
					return allNodes;
				});
				updateNodeInternals(task.id!);
			}
			setIsCanvasDirty(true);
		},
		[metadata, formik, flowInstance, updateNodeInternals, setIsCanvasDirty]
	);

	const onDeleteTasks = useCallback(
		(tasks: Task[]) => {
			const newScreenState = { ...formik.values.screens };
			for (const task of tasks) {
				const { screenId } = task;
				if (screenId) {
					delete newScreenState[screenId];
				}
			}
			formik.setFieldValue('screens', newScreenState);
			// remove all leftover interactions from existing nodes.
			const allTasks = getAllTasks(flowInstance);
			tasks.forEach((t) => {
				if (t.id) delete allTasks[t.id];
			});
			flowInstance?.setNodes((allNodes) =>
				allNodes
					.filter((n) => Object.keys(allTasks).includes(n.id))
					.map((n) => ({
						...n,
						data: {
							...n.data,
							interactions: (n.data as ITaskNodeData)?.task
								? getTaskInteractions(
										(n.data as ITaskNodeData)?.task!,
										allTasks,
										metadata,
										formik.values.screens
								  )
								: n.data.interactions
						}
					}))
			);
			setIsCanvasDirty(true);
		},
		[flowInstance, metadata, formik, setIsCanvasDirty]
	);

	useEffect(() => {
		if (screens) {
			formik.setFieldValue('screens', screens);
			if (postSaveRefetch && flowInstance && flow) {
				// update nodes with new attributes such as new screen id.
				flowInstance.setNodes((allNodes) =>
					allNodes.map((node) => {
						const task = flow.dsl.tasks?.[node.id];
						if (task && task.screenId) {
							task.id = node.id;
							return {
								...node,
								data: {
									...node.data,
									task,
									label: task.name,
									interactions: getTaskInteractions(
										task,
										flow.dsl.tasks,
										metadata,
										screens
									)
								}
							};
						}
						return node;
					})
				);
				setPostSaveRefetch(false);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [screens, postSaveRefetch, setPostSaveRefetch, metadata, flowInstance]);

	const setUpdateScreen = useCallback(
		(func: () => IScreenMetadata) => {
			setUpdateScreenCallback(() => func);
		},
		[setUpdateScreenCallback]
	);

	const onTaskDialogDone = useCallback(
		(task: Task) => {
			updateTask(task);
			setEditingTask(null);
		},
		[updateTask, setEditingTask]
	);

	const onTaskDialogCancel = useCallback(
		() => setEditingTask(null),
		[setEditingTask]
	);

	const onSave = useCallback(() => {
		if (!editingTask) {
			formik.handleSubmit();
			return;
		}

		screenFormik.handleSubmit();
	}, [editingTask, formik, screenFormik]);

	const onViewChange = useCallback(() => {
		if (!isCanvasDirty) setIsCanvasDirty(true);
	}, [isCanvasDirty, setIsCanvasDirty]);

	const onScreensAdded = useCallback(
		(newScreens: IFlowScreens) => {
			formik.setFieldValue('screens', {
				...formik.values.screens,
				...newScreens
			});
		},
		[formik]
	);

	const isEditingScreenTask = !!(
		editingTask && editingTask.type === TaskType.screen
	);
	const isEndTask = editingTask?.action === END_TASK_ID;
	const isDirty = formik.dirty || isCanvasDirty || isScreenDirty;

	useEditorKeyboard(isEditingScreenTask ? noop : formik.handleSubmit);

	const handleExport = useCallback(async () => {
		const exported = await exportFlow(flow?.id!);
		downloadAsFile(exported, `${flow?.id}.json`);
	}, [flow]);

	const handleImport = useCallback(
		async (data: IExportedFlow) =>
			importFlowMutation.mutateAsync({ ...data, flowId: flow?.id! }),
		[flow, importFlowMutation]
	);

	return (
		<Box
			data-testid="flow-builder-page"
			id="flow-builder-page"
			display="flex"
			flexGrow={1}
			flexDirection="column"
			overflow="hidden"
		>
			<NavigationApprovalDialog hasUnsavedChanges={isDirty} />
			{isEditingScreenTask ? (
				<Box
					display="flex"
					flexDirection="column"
					position="absolute"
					top={0}
					bottom={0}
					left={0}
					right={0}
					zIndex={1000}
				>
					<PageEditor
						initialScreen={formik.values.screens?.[editingTask.screenId!]}
						updatePageCallback={setUpdateScreen}
						onDirty={setIsScreenDirty}
						header={
							<FlowBuilderHeader
								onCancel={onTaskDialogCancel}
								flowName={formik.values.name}
								flowId={flow?.id}
								disabled={flow?.disabled}
								formik={screenFormik}
								isEditingScreenTask
								onSave={onSave}
								isDirty={isDirty}
								buttons={<ActionsBar />}
							/>
						}
					/>
				</Box>
			) : (
				<FlowBuilderHeader
					onCancel={onTaskDialogCancel}
					formik={formik}
					disabled={flow?.disabled}
					flowId={flow?.id}
					isDirty={isDirty}
					isLoading={isLoading}
					onSave={onSave}
					buttons={
						<ExportImportButtons
							onExport={handleExport}
							onImportCompleted={resetKey}
							onImport={handleImport}
							toastLabelPrefix="flows.builder.import"
							question={intl.tr('flows.builder.import.approve.question')}
							title={intl.tr('flows.builder.import.approve.title')}
						/>
					}
				/>
			)}
			<Box display="flex" overflow="hidden" flexGrow={1} position="relative">
				<Loader variant="rectangular" isLoading={isLoading}>
					<>
						{editingTask &&
							!isEndTask &&
							editingTask.type === TaskType.automated && (
								<FlowTaskDialog
									onDone={onTaskDialogDone}
									task={editingTask}
									metadata={metadata}
									onCancel={onTaskDialogCancel}
								/>
							)}
						{editingTask && editingTask.type === TaskType.condition && (
							<TaskConditionsDialog
								onDone={(name, conditions) => {
									const newTask = { ...editingTask, name, conditions };
									onTaskDialogDone(newTask);
								}}
								conditions={metadata ? metadata.conditions : {}}
								taskConditions={editingTask.conditions}
								taskName={editingTask.name}
								onCancel={onTaskDialogCancel}
							/>
						)}
						<FlowView
							key={key}
							initialNodes={initialNodes}
							initialEdges={initialEdges}
							onChange={onViewChange}
							setRef={setFlowInstance}
							options={{
								deleteKeyCode: isEditingScreenTask ? null : 'Backspace'
							}}
							onEditTask={setEditingTask}
							onDeleteTasks={onDeleteTasks}
							onScreenAdd={onScreensAdded}
							metadata={metadata}
						/>
					</>
				</Loader>
			</Box>
		</Box>
	);
};

FlowBuilder.defaultProps = {
	flow: undefined,
	screens: undefined,
	metadata: undefined
};

export default FlowBuilder;
